import { Quaternion, Vector3 } from 'three';
import { OneEuroQuaternion } from './OneEuroQuaternion';
import { OneEuroVector3 } from './OneEuroVector3';
import { OneEuroFilter, type OneEuroParams } from './OneEuroFilter';

export interface RawPose {
  /** Frame anchor position in camera space, millimetres (X right, Y up, Z towards camera → negative). */
  position: Vector3;
  /** Frame orientation in camera space. */
  quaternion: Quaternion;
  /** Sample time in seconds (monotonic clock shared with `evaluate`). */
  tSeconds: number;
}

export interface SmoothedPose {
  position: Vector3;
  quaternion: Quaternion;
  /** True while the pose is extrapolated without fresh samples. */
  coasting: boolean;
  /** True when the head is considered still (stronger smoothing engaged). */
  still: boolean;
  /** Milliseconds of prediction applied on top of the last sample. */
  predictedMs: number;
}

export interface PoseSmootherParams {
  /** Filter for X/Y (mm). */
  position: OneEuroParams;
  /** Filter for Z / depth (mm). Depth is noisier: lower cutoff. */
  depth: OneEuroParams;
  /** Filter for rotation (rad/s drives beta). */
  rotation: OneEuroParams;
  /** Stillness lock: engage extra smoothing when the head is still. */
  stillness: {
    enabled: boolean;
    /** Linear speed threshold (mm/s) below which the head is "still". */
    speedMmPerS: number;
    /** Angular speed threshold (rad/s). */
    angularRadPerS: number;
    /** How long (ms) the head must be still before locking. */
    holdMs: number;
    /** Multiplier applied to minCutoff while locked (0.3 = 3x smoother). */
    cutoffScale: number;
  };
  /** Latency compensation: extrapolate the pose by (render time - sample time) * gain. */
  prediction: {
    enabled: boolean;
    /** 0..1 fraction of the measured latency to compensate. */
    gain: number;
    /** Never predict more than this (ms) — protects against stalls. */
    maxMs: number;
  };
  /**
   * Filter-lag compensation. A first-order low-pass trails a moving target by
   * its time constant 1/(2π·cutoff); extrapolating by that much along the
   * filtered velocity removes the trail at constant velocity, which is what
   * makes the frame feel glued to the face instead of floating behind it.
   */
  lagCompensation: {
    enabled: boolean;
    /** 0..1 fraction of the time constant to compensate. */
    gain: number;
    /** Cap (ms): protects against over-shoot when the cutoff is very low (rest). */
    maxMs: number;
    /**
     * A110Y — porte douce : la compensation ne s'applique qu'en mouvement avéré.
     * Elle passe de 0 à 1 entre `gateSpeedMmPerS` et 2× cette vitesse (XY), entre
     * `gateAngularRadPerS` et 2× (rotation), entre `gateDepthMmPerS` et 2× (Z).
     * En dessous, la vitesse filtrée n'est que du bruit : la corriger « vers le
     * brut » ré-injecte exactement le bruit que le filtre venait d'ôter.
     */
    gateSpeedMmPerS: number;
    gateAngularRadPerS: number;
    gateDepthMmPerS: number;
  };
  /** Behaviour when samples stop arriving (tracking loss). */
  coast: {
    /** Stop extrapolating after this many ms without a sample. */
    maxMs: number;
    /** Exponential decay time constant of the velocity (ms). */
    dampingTauMs: number;
  };
}

/**
 * Defaults tuned for "glued to the face": at rest the cutoffs are low enough
 * to kill landmark jitter (< 0.5 px), and as soon as the head moves the
 * adaptive term (beta) opens the filters wide — 200 mm/s → ~13 Hz on position,
 * 1 rad/s → ~4 Hz on rotation — while the lag compensation cancels what little
 * trail remains. The derivative estimators run at 4 Hz so that the speed
 * (and therefore the responsiveness) reacts within ~40 ms of a movement onset.
 */
export const DEFAULT_POSE_SMOOTHER_PARAMS: PoseSmootherParams = {
  // Tuned for "follows the head instantly" now that the position anchor is
  // fitted over ~120 landmarks (raw noise divided by ~10): higher floor
  // cutoffs, full latency and filter-lag compensation, a lighter stillness lock.
  position: { minCutoff: 2.0, beta: 0.12, dCutoff: 4.0 },
  // A110Y — la profondeur MediaPipe est bruitée (≈ 1–2 % image par image) : beta
  // 0,03 × 150 mm/s de « vitesse-bruit » ouvrait le filtre à ~5 Hz au repos.
  depth: { minCutoff: 0.6, beta: 0.006, dCutoff: 0.7 },
  rotation: { minCutoff: 2.5, beta: 3.5, dCutoff: 4.0 },
  stillness: { enabled: true, speedMmPerS: 10, angularRadPerS: 0.04, holdMs: 500, cutoffScale: 0.7 },
  prediction: { enabled: true, gain: 1.0, maxMs: 90 },
  lagCompensation: { enabled: true, gain: 1.0, maxMs: 120, gateSpeedMmPerS: 10, gateAngularRadPerS: 0.2, gateDepthMmPerS: 80 },
  coast: { maxMs: 400, dampingTauMs: 80 },
};

/**
 * Adaptive pose smoother: One Euro filters on position, depth and rotation,
 * a stillness lock (extra smoothing when the head does not move), short-horizon
 * prediction (latency compensation) and damped extrapolation during brief
 * tracking losses.
 *
 * Contract: `push()` once per tracker result, `evaluate()` once per rendered
 * frame. Both use the same monotonic clock (performance.now() / 1000).
 */
export class PoseSmoother {
  private params: PoseSmootherParams;
  private readonly xyFilter: OneEuroVector3;
  private readonly zFilter: OneEuroFilter;
  private readonly rotFilter: OneEuroQuaternion;

  private readonly lastPosition = new Vector3();
  private readonly lastQuaternion = new Quaternion();
  private readonly velocity = new Vector3(); // mm/s (filtered)
  private readonly angular = new Vector3(); // axis * rad/s
  private readonly lastRawPosition = new Vector3();
  private readonly lastRawQuaternion = new Quaternion();
  /** Instantaneous speed of the raw input (mm/s): drops before the filtered velocity when the head stops. */
  private rawSpeed = 0;
  /** A110Y — vitesse brute en profondeur (mm/s), pour la décélération de l'axe Z. */
  private rawSpeedZ = 0;
  private lastSampleTime = -1;
  private hasSample = false;

  private stillSince = -1;
  private still = false;
  /** Measured interval between samples (ms); coasting adapts to slow inference. */
  private sampleIntervalMs = 33;

  private readonly tmpV = new Vector3();
  private readonly tmpQ = new Quaternion();
  private readonly xyIn = new Vector3();
  private readonly xyOut = new Vector3();

  constructor(params: Partial<PoseSmootherParams> = {}) {
    this.params = mergeParams(DEFAULT_POSE_SMOOTHER_PARAMS, params);
    this.xyFilter = new OneEuroVector3(this.params.position);
    this.zFilter = new OneEuroFilter(this.params.depth);
    this.rotFilter = new OneEuroQuaternion(this.params.rotation);
  }

  getParams(): PoseSmootherParams {
    return JSON.parse(JSON.stringify(this.params)) as PoseSmootherParams;
  }

  setParams(params: Partial<PoseSmootherParams>): void {
    this.params = mergeParams(this.params, params);
    this.applyFilterParams(1);
  }

  private applyFilterParams(cutoffScale: number): void {
    const p = this.params;
    this.xyFilter.setParams({ ...p.position, minCutoff: p.position.minCutoff * cutoffScale });
    this.zFilter.setParams({ ...p.depth, minCutoff: p.depth.minCutoff * cutoffScale });
    this.rotFilter.setParams({ ...p.rotation, minCutoff: p.rotation.minCutoff * cutoffScale });
  }

  /**
   * Tell the smoother how often samples arrive (ms). On a slow device where
   * inference takes 100+ ms, coasting must outlive the gap between samples or
   * the frame would flicker; prediction horizons stay bounded by `maxMs`.
   */
  setSampleIntervalMs(ms: number): void {
    if (Number.isFinite(ms) && ms > 0) this.sampleIntervalMs = ms;
  }

  /** Effective coasting duration: the configured value or 2.5 sample intervals, whichever is longer. */
  get effectiveCoastMs(): number {
    return Math.max(this.params.coast.maxMs, this.sampleIntervalMs * 2.5);
  }

  /** Feed a raw pose sample from the tracker. */
  push(sample: RawPose): void {
    const t = sample.tSeconds;
    // A110Y — a non-finite sample (video reported 0×0, a degenerate solve) would poison every
    // filter until the next reset(): dropped.
    if (!(Number.isFinite(t) && isFiniteVec(sample.position) && isFiniteQuat(sample.quaternion))) return;
    if (this.hasSample && t > this.lastSampleTime) {
      // Track the real cadence (EMA) so coasting adapts to slow devices.
      // A110Y — un trou (perte de visage, calage, veille courte) n'est pas une
      // cadence : sans ce garde-fou, un trou de 3 s portait l'intervalle estimé à
      // 600 ms (coasting 1,5 s, seuil 0,9 s) pendant une quinzaine d'échantillons.
      const gapMs = (t - this.lastSampleTime) * 1000;
      if (gapMs < Math.max(250, this.sampleIntervalMs * 3)) this.sampleIntervalMs = this.sampleIntervalMs * 0.8 + gapMs * 0.2;
      // A110Y — XY seulement (la profondeur brute saute de ±1–2 % d'une image à l'autre).
      const sp = NOMINAL_DEPTH_MM / Math.max(1, -sample.position.z);
      this.rawSpeed = Math.hypot(sample.position.x * sp - this.lastRawPosition.x, sample.position.y * sp - this.lastRawPosition.y) / (t - this.lastSampleTime);
      this.rawSpeedZ = Math.abs(sample.position.z - this.lastRawPosition.z) / (t - this.lastSampleTime);
    } else {
      this.rawSpeed = 0;
      this.rawSpeedZ = 0;
    }
    // A110Y — repère de filtrage : le plan image, pas l'espace caméra en mm.
    // X = (u − cx)·Z/f : tout le bruit de profondeur (±1–2 % par image) devient
    // du bruit de position dès que le visage n'est pas au centre (300 px de
    // décentrage → ±2 px). Filtrer (x/Z, y/Z) et Z séparément garde la position
    // écran là où les landmarks la mesurent ; Z ne joue plus que sur la taille.
    // Unités : « mm à la distance nominale NOMINAL_DEPTH_MM » (les réglages
    // minCutoff/beta gardent leur sens).
    const sPlane = NOMINAL_DEPTH_MM / Math.max(1, -sample.position.z);
    this.lastRawPosition.set(sample.position.x * sPlane, sample.position.y * sPlane, sample.position.z);
    this.lastRawQuaternion.copy(sample.quaternion);

    // XY and Z are filtered separately (depth is noisier and slower).
    this.xyIn.set(this.lastRawPosition.x, this.lastRawPosition.y, 0);
    this.xyFilter.filter(this.xyIn, t, this.xyOut);
    const z = this.zFilter.filter(sample.position.z, t);
    this.lastPosition.set(this.xyOut.x, this.xyOut.y, z);
    this.rotFilter.filter(sample.quaternion, t, this.lastQuaternion);

    const v = this.xyFilter.velocity;
    this.velocity.set(v.x, v.y, this.zFilter.derivative);
    this.angular.copy(this.rotFilter.angularVelocity);

    this.updateStillness(t);
    this.lastSampleTime = t;
    this.hasSample = true;
  }

  private updateStillness(t: number): void {
    const s = this.params.stillness;
    if (!s.enabled) {
      if (this.still) {
        this.still = false;
        this.applyFilterParams(1);
      }
      return;
    }
    // A110Y — vitesse XY seulement : la dérivée de profondeur est du bruit
    // (±100 mm/s au repos avec 1 % de bruit MediaPipe) et interdisait le verrou.
    const speed = Math.hypot(this.velocity.x, this.velocity.y);
    const angular = this.angular.length();
    // A110Y — la vitesse angulaire brute (moyenne de |Δθ|/dt) a un plancher de
    // bruit ≈ 2,26·σ/dt (0,3 rad/s pour σ = 0,25° à 30 fps) : jamais sous 0,08.
    // On juge sur la vitesse de la sortie filtrée, et on ne garde la brute que
    // comme déclencheur de sortie, à un seuil nettement au-dessus du plancher.
    const isSlow = speed < s.speedMmPerS && angular < s.angularRadPerS;
    if (isSlow) {
      if (this.stillSince < 0) this.stillSince = t;
      if (!this.still && (t - this.stillSince) * 1000 >= s.holdMs) {
        this.still = true;
        this.applyFilterParams(s.cutoffScale);
      }
    } else {
      // Small hysteresis only: the lock must release the instant the head really
      // moves — judged on the raw (unfiltered) speeds too, which lead the filtered ones.
      const leaving = speed > s.speedMmPerS * 1.3 || angular > s.angularRadPerS * 1.3 || this.rotFilter.speed > 0.6 || this.rawSpeed > s.speedMmPerS * 2.5;
      if (leaving || !this.still) {
        this.stillSince = -1;
        if (this.still) {
          this.still = false;
          this.applyFilterParams(1);
        }
      }
    }
  }

  /**
   * Pose to render at time `nowSeconds`. Returns false when there is no usable
   * pose (never tracked, or coasting expired).
   */
  evaluate(nowSeconds: number, out: SmoothedPose, holdExpired = false): boolean {
    if (!this.hasSample) return false;
    const sinceSample = Math.max(0, nowSeconds - this.lastSampleTime); // seconds
    const sinceMs = sinceSample * 1000;
    const p = this.params;

    if (sinceMs > this.effectiveCoastMs) {
      planeToCamera(this.lastPosition, out.position);
      out.quaternion.copy(this.lastQuaternion);
      out.still = this.still;
      out.predictedMs = 0;
      out.coasting = true;
      // The app state machine owns the stale fade; never extrapolate this gap.
      return holdExpired;
    }

    out.position.copy(this.lastPosition);
    out.quaternion.copy(this.lastQuaternion);
    out.still = this.still;
    out.coasting = false;
    out.predictedMs = 0;

    // Effective horizon: latency compensation (gain-scaled, clamped) while
    // fresh samples flow, damped extrapolation when they stop.
    let horizon = 0;
    if (this.still) {
      horizon = 0;
    } else if (p.prediction.enabled) {
      horizon = Math.min(sinceSample * p.prediction.gain, p.prediction.maxMs / 1000);
    }

    // Beyond ~1.5 sample intervals without a sample, treat as coasting: use the
    // damped integral of the velocity instead of a linear prediction.
    // A110Y — horizon CONTINU : avant, au passage du seuil l'horizon sautait de
    // `since` (ou 90 ms) à τ·(1−e^(−since/τ)) (≈ 43 ms à 62 ms) : un recul de
    // 19 ms × vitesse à chaque image sur deux en mode vidéo directe (dents de scie).
    // Ici la prédiction linéaire continue jusqu'au seuil, puis l'extrapolation
    // s'amortit à partir de là ; `coastDamping` (1 → 0) éteint aussi la
    // compensation de traînée en douceur au lieu de la couper.
    const coastThresholdMs = Math.max(50, this.sampleIntervalMs * 1.5);
    let coastDamping = 1;
    if (sinceMs > coastThresholdMs) {
      out.coasting = true;
      const tau = p.coast.dampingTauMs / 1000;
      const t0 = coastThresholdMs / 1000;
      const beyond = sinceSample - t0;
      coastDamping = Math.exp(-beyond / tau);
      if (p.prediction.enabled && !this.still) {
        const linear = Math.min(t0 * p.prediction.gain, p.prediction.maxMs / 1000);
        horizon = linear + tau * (1 - coastDamping) * p.prediction.gain;
      } else {
        horizon = tau * (1 - coastDamping);
      }
    }

    // Filter-lag compensation, per filter (position and rotation run at their
    // own cutoffs). Faded out while the raw input is slowing down faster than
    // the filtered velocity (a stop): extrapolating there would overshoot.
    // A110Y — portes douces sur la vitesse : au repos la vitesse filtrée n'est
    // que du bruit et la « correction bornée au résidu » ramenait la sortie sur
    // l'échantillon brut (le filtre était contourné : 0,27 px rendu pour 0,27 px brut).
    let posHorizon = horizon;
    let depthHorizon = horizon;
    let rotHorizon = horizon;
    const g = p.lagCompensation;
    // A110Y — la profondeur n'a pas besoin de prédiction de latence (45 ms × 150 mm/s
    // = 7 mm = 1,5 % d'échelle, invisible) ; sa vitesse filtrée est surtout du bruit.
    const depthGate = clamp01((Math.abs(this.velocity.z) - g.gateDepthMmPerS) / Math.max(1e-3, g.gateDepthMmPerS)) * coastDamping;
    if (g.enabled) {
      const maxS = g.maxMs / 1000;
      const speedXY = Math.hypot(this.velocity.x, this.velocity.y);
      const posDecel = clamp01(this.rawSpeed / Math.max(1e-3, speedXY));
      const depthDecel = clamp01(this.rawSpeedZ / Math.max(1e-3, Math.abs(this.velocity.z)));
      const rotDecel = clamp01(this.rotFilter.rawSpeed / Math.max(1e-3, this.angular.length()));
      // Le verrou d'immobilité (XY + rotation) ne juge pas la profondeur : on peut
      // s'approcher sans bouger latéralement ; la porte Z a son propre seuil.
      const moving = this.still ? 0 : 1;
      const posGate = clamp01((speedXY - g.gateSpeedMmPerS) / Math.max(1e-3, g.gateSpeedMmPerS)) * coastDamping * moving;
      const rotGate = clamp01((this.angular.length() - g.gateAngularRadPerS) / Math.max(1e-3, g.gateAngularRadPerS)) * coastDamping * moving;
      posHorizon += Math.min(maxS, filterLagSeconds(this.xyFilter.currentCutoff) * g.gain) * posDecel * posGate;
      depthHorizon += Math.min(maxS, filterLagSeconds(this.zFilter.currentCutoff) * g.gain) * depthDecel * depthGate;
      rotHorizon += Math.min(maxS, filterLagSeconds(this.rotFilter.currentCutoff) * g.gain) * rotDecel * rotGate;
    }

    if (posHorizon > 0 || depthHorizon > 0) {
      // Depth has its own adaptive cutoff: using the XY lag here causes a
      // scale overshoot when the wearer approaches the camera.
      this.tmpV.set(this.velocity.x * posHorizon, this.velocity.y * posHorizon, this.velocity.z * depthHorizon * depthGate);
      // At capture time compensation may close the measured filter residual,
      // but must not run beyond it or in the old direction after a reversal.
      // Only the true camera-to-render horizon may extrapolate beyond a sample.
      for (const axis of ['x', 'y', 'z'] as const) {
        const future = this.velocity[axis] * horizon * (axis === 'z' ? depthGate : 1);
        const correction = this.tmpV[axis] - future;
        const residual = this.lastRawPosition[axis] - this.lastPosition[axis];
        this.tmpV[axis] = future + boundedCorrection(correction, residual);
      }
      out.position.add(this.tmpV);
    }
    if (rotHorizon > 0) {
      const w = this.angular.length();
      if (w > 1e-4) {
        this.tmpV.copy(this.angular).divideScalar(w);
        const residualAngle = this.lastQuaternion.angleTo(this.lastRawQuaternion);
        const correctionAngle = Math.min(residualAngle, w * Math.max(0, rotHorizon - horizon));
        // Follow the shortest measured residual for lag correction; retain the
        // angular velocity only for genuine future prediction.
        if (residualAngle > 1e-6) out.quaternion.slerp(this.lastRawQuaternion, correctionAngle / residualAngle);
        this.tmpQ.setFromAxisAngle(this.tmpV, w * horizon);
        out.quaternion.premultiply(this.tmpQ).normalize();
      }
    }
    out.predictedMs = Math.max(posHorizon, depthHorizon, rotHorizon) * 1000;
    planeToCamera(out.position, out.position);
    return true;
  }

  get isStill(): boolean {
    return this.still;
  }

  get lastSampleSeconds(): number {
    return this.lastSampleTime;
  }

  reset(): void {
    this.xyFilter.reset();
    this.zFilter.reset();
    this.rotFilter.reset();
    this.hasSample = false;
    this.lastSampleTime = -1;
    this.still = false;
    this.stillSince = -1;
    this.velocity.set(0, 0, 0);
    this.angular.set(0, 0, 0);
    this.rawSpeed = 0;
    this.rawSpeedZ = 0;
    this.applyFilterParams(1);
    this.sampleIntervalMs = 33;
  }
}

/** Distance nominale (mm) : les coordonnées plan sont exprimées en « mm à cette distance ». */
const NOMINAL_DEPTH_MM = 450;

/** (px, py, z) plan-image → (x, y, z) caméra, mm. */
function planeToCamera(plane: Vector3, out: Vector3): Vector3 {
  const z = plane.z;
  const k = Math.max(1, -z) / NOMINAL_DEPTH_MM;
  return out.set(plane.x * k, plane.y * k, z);
}

function isFiniteVec(v: Vector3): boolean {
  return Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
}
function isFiniteQuat(q: Quaternion): boolean {
  return Number.isFinite(q.x) && Number.isFinite(q.y) && Number.isFinite(q.z) && Number.isFinite(q.w);
}

function boundedCorrection(correction: number, residual: number): number {
  if (correction * residual <= 0) return 0;
  return Math.sign(residual) * Math.min(Math.abs(correction), Math.abs(residual));
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}


/** Time constant of a first-order low-pass at `cutoffHz` (seconds); 0 when unknown. */
function filterLagSeconds(cutoffHz: number): number {
  return cutoffHz > 1e-6 ? 1 / (2 * Math.PI * cutoffHz) : 0;
}

function mergeParams(base: PoseSmootherParams, patch: Partial<PoseSmootherParams>): PoseSmootherParams {
  return {
    position: { ...base.position, ...(patch.position ?? {}) },
    depth: { ...base.depth, ...(patch.depth ?? {}) },
    rotation: { ...base.rotation, ...(patch.rotation ?? {}) },
    stillness: { ...base.stillness, ...(patch.stillness ?? {}) },
    prediction: { ...base.prediction, ...(patch.prediction ?? {}) },
    lagCompensation: { ...base.lagCompensation, ...(patch.lagCompensation ?? {}) },
    coast: { ...base.coast, ...(patch.coast ?? {}) },
  };
}
